import { useState } from 'react'
import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { BetDock } from './BetDock'
import type { Phase } from '../ws/types'

const QUICK = [10, 50, 100, 500, 1000]

type Props = {
  phase: Phase
  balance: number
  hasBet: boolean
  cashed: boolean
  confirmedAutoCashoutAt?: number
  connected: boolean
  pendingAction: 'bet' | 'cashout' | null
  balanceOutOfSync: boolean
  balanceRefreshing: boolean
  settlementBlocked: boolean
  onRefreshBalance: () => void
  onBet: (amount: number, autoCashoutAt: number) => void
  onCashout: () => void
  error: string | null
  bettingEndsIn?: number | null
}

export function BetPanel({
  phase,
  balance,
  hasBet,
  cashed,
  confirmedAutoCashoutAt,
  connected,
  pendingAction,
  balanceOutOfSync,
  balanceRefreshing,
  settlementBlocked,
  onRefreshBalance,
  onBet,
  onCashout,
  error,
  bettingEndsIn = null,
}: Props) {
  const { t } = useTranslation()
  const [amount, setAmount] = useState(100)
  const [autoAt, setAutoAt] = useState(0)
  const [showChips, setShowChips] = useState(false)
  const displayedAutoAt = hasBet ? (confirmedAutoCashoutAt ?? 0) : autoAt
  const canBet = connected && phase === 'betting' && !hasBet && !pendingAction && !balanceOutOfSync && !settlementBlocked
  const canCash = connected && phase === 'running' && hasBet && !cashed && !pendingAction && !settlementBlocked

  const phaseLabel = (p: Phase): string => {
    switch (p) {
      case 'betting':
        return t('phase.betting')
      case 'running':
        return t('phase.running')
      case 'crashed':
        return t('phase.crashed')
      default:
        return t('phase.waiting')
    }
  }

  // Tier 1: Auto cashout parameter selector
  const parameterSlot = (
    <div className="bet-dock-segmented" style={{ padding: '3px 8px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <span style={{ fontSize: '0.74rem', color: 'var(--text-secondary)', fontWeight: 700 }}>
        {t('autoCashout')}
        {hasBet && displayedAutoAt >= 1.01
          ? ` · ${displayedAutoAt.toFixed(2)}x`
          : ''}
      </span>
      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        {[0, 1.5, 2, 5].map((val) => (
          <button
            key={val}
            type="button"
            className={`bet-dock-segment-btn${displayedAutoAt === val ? ' active' : ''}`}
            style={{ padding: '3px 7px', minWidth: 44, fontSize: '0.75rem' }}
            aria-pressed={displayedAutoAt === val}
            disabled={!canBet}
            onClick={() => setAutoAt(val)}
          >
            {val === 0 ? t('off') : `${val}x`}
          </button>
        ))}
      </div>
    </div>
  )

  // Tier 2: Amount input and collapsible quick chips
  const amountSlot = (
    <div className="bet-dock-tier-amount">
      <div className="bet-dock-amount-main-row">
        <div className="bet-dock-input-group">
          <label htmlFor="bet-amount">{t('betAmount')}</label>
          <input
            id="bet-amount"
            type="number"
            min={10}
            max={balance || 100000}
            value={amount}
            disabled={!canBet}
            onChange={(e) => setAmount(Math.max(0, Number(e.target.value) || 0))}
          />
        </div>
        <div className="bet-dock-quick-multipliers">
          <button
            type="button"
            className="bet-dock-multiplier-btn"
            disabled={!canBet}
            onClick={() => setAmount(Math.max(10, Math.floor(amount / 2)))}
          >
            1/2
          </button>
          <button
            type="button"
            className="bet-dock-multiplier-btn"
            disabled={!canBet}
            onClick={() => setAmount(Math.max(10, Math.min(balance, amount * 2)))}
          >
            2X
          </button>
          <button
            type="button"
            className="bet-dock-multiplier-btn"
            disabled={!canBet}
            onClick={() => setAmount(Math.max(10, balance))}
          >
            {t('max')}
          </button>
          <button
            type="button"
            className={`bet-dock-chip-toggle${showChips ? ' active' : ''}`}
            disabled={!canBet}
            onClick={() => setShowChips(!showChips)}
            title={t('quickChips')}
            aria-label={t('quickChips')}
            aria-expanded={showChips}
            aria-controls="crash-quick-chips"
          >
            {showChips ? '▲' : '⚡'}
          </button>
        </div>
      </div>
      {showChips && (
        <div id="crash-quick-chips" className="bet-dock-collapsible-chips">
          {QUICK.map((q) => (
            <button
              key={q}
              type="button"
              className="bet-dock-preset-chip"
              disabled={!canBet}
              onClick={() => {
                setAmount(q)
                setShowChips(false)
              }}
            >
              {q}
            </button>
          ))}
        </div>
      )}
    </div>
  )

  // Tier 3: Primary CTA
  let ctaStateClass = 'state-ready'
  if (settlementBlocked) ctaStateClass = 'state-danger'
  else if (canCash) ctaStateClass = 'state-cashout pulse'
  else if (phase === 'crashed') ctaStateClass = 'state-danger'

  const ctaLabel = settlementBlocked
    ? t('settlementNeedsReview')
    : pendingAction === 'cashout'
      ? t('cashingOut')
      : pendingAction === 'bet'
        ? t('submittingBet')
        : canCash
          ? t('cashout')
          : balanceRefreshing
            ? t('refreshingBalance')
            : balanceOutOfSync
              ? t('retryBalance')
              : phase === 'betting'
                ? t('placeBet')
                : t('waitNext')

  const ctaSlot = (
    <motion.button
      type="button"
      className={`bet-dock-cta-btn ${ctaStateClass}`}
      whileTap={{ scale: 0.98 }}
      disabled={
        Boolean(pendingAction) ||
        settlementBlocked ||
        (canCash
          ? false
          : balanceOutOfSync
            ? balanceRefreshing || !connected
            : !canBet || amount < 10 || amount > balance)
      }
      onClick={() => {
        if (canCash) onCashout()
        else if (balanceOutOfSync) onRefreshBalance()
        else if (canBet) onBet(amount, autoAt)
      }}
    >
      {ctaLabel}
    </motion.button>
  )

  const metaSlot = (
    <>
      <span>{t('balance', { amount: Math.round(balance) })}</span>
      <span>
        {phase === 'betting' && bettingEndsIn != null
          ? t('bettingLeft', { sec: bettingEndsIn.toFixed(1) })
          : phaseLabel(phase)}
      </span>
    </>
  )

  return (
    <BetDock
      className="crash-bet-dock"
      parameterSlot={parameterSlot}
      amountSlot={amountSlot}
      ctaSlot={ctaSlot}
      metaSlot={metaSlot}
      error={error}
    />
  )
}
